<script setup lang="ts">
import { computed, CSSProperties, ref } from "vue";
import { HideAndShowHelper } from "@/models/hide-and-show-helper";

defineOptions({ name: "BcHideAndShow" });

defineProps({
  disableAnimation: { type: Boolean, default: false },
});

const isScrolling = ref(false);

const scrollTimeoutId = ref<any>(null);
const delayTimeoutId = ref<any>(null);

const ref_hideAndShowInner = ref<HTMLDivElement | null>(null);
const ref_hideAndShowComponent = ref<HTMLDivElement | null>(null);

const scrollStyle = computed<CSSProperties>(() => {
  if (!isScrolling.value) return {};

  let textWidth = getTextWidth();
  let animationDurations = getAnimationDurations();
  let animationDelays = getAnimationDelays(animationDurations);
  let timeoutDuration = getTimeoutDuration(animationDurations, animationDelays);

  scrollTimeoutId.value = setTimeout(() => {
    clearTimeout(scrollTimeoutId.value ?? undefined);
    isScrolling.value = false;
  }, timeoutDuration);

  return {
    "--textWidth": `${textWidth}`,
    "--animationDelays": `${animationDelays.moveRight}s, ${animationDelays.stayRight}s, ${animationDelays.moveLeft}s`,
    "--animationDurations": `${animationDurations.moveRight}s, ${animationDurations.stayRight}s, ${animationDurations.moveLeft}s`,
  };
});

function onMouseEnter(): void {
  if (
    ref_hideAndShowComponent.value &&
    HideAndShowHelper.isScrollable(ref_hideAndShowComponent.value) &&
    !isScrolling.value
  ) {
    startDelayedAutoScroll();
  }
}

function onTouchStart(): void {
  if (
    ref_hideAndShowComponent.value &&
    HideAndShowHelper.isScrollable(ref_hideAndShowComponent.value) &&
    !isScrolling.value
  ) {
    startDelayedAutoScroll();
  }
}

function startDelayedAutoScroll(): void {
  const delayTime_ms = 1000;
  delayTimeoutId.value = setTimeout(() => {
    clearTimeout(delayTimeoutId.value ?? undefined);
    isScrolling.value = true;
  }, delayTime_ms);
}

function stopDelayedAutoScroll(): void {
  clearTimeout(delayTimeoutId.value ?? undefined);
}

function isEllipsisScrollable(): boolean {
  const hideAndShow = ref_hideAndShowInner.value as HTMLDivElement | null;
  if (!hideAndShow) return false;
  return hideAndShow.scrollWidth === hideAndShow.clientWidth;
}

function getTextWidth(): string {
  if (isEllipsisScrollable()) return `0%`;

  const hideAndShow = ref_hideAndShowInner.value as HTMLDivElement;
  return `calc(${hideAndShow.clientWidth}px - 100%)`;
}

function getAnimationDurations() {
  if (isEllipsisScrollable()) {
    return {
      moveRight: 0,
      stayRight: 1,
      moveLeft: 0,
    };
  }

  const textSpeed = 0.05; // 50 Pixels per second = 0.05 px/millisecond
  const hideAndShow = ref_hideAndShowInner.value as HTMLDivElement;
  const width_px = Math.abs(hideAndShow.offsetWidth - hideAndShow.scrollWidth);
  return {
    moveRight: width_px / textSpeed / 2000, // Making it faster -> /2
    stayRight: 1, // fixed 1000ms
    moveLeft: 0.25, // fixed 250ms
  };
}

function getAnimationDelays(animationDurations: any) {
  return {
    moveRight: 0,
    stayRight: animationDurations.moveRight,
    moveLeft: animationDurations.moveRight + animationDurations.stayRight,
  };
}

function getTimeoutDuration(animationDurations: any, animationDelays: any) {
  const duration_ms = animationDurations.moveLeft + animationDelays.moveLeft;
  const duration_s = duration_ms * 1000;
  const offset_s = 0.2;
  return duration_s + offset_s;
}

defineExpose({
  isScrolling,
  ref_hideAndShowInner,
  ref_hideAndShowComponent,
  scrollStyle,
});
</script>

<template>
  <div
    ref="ref_hideAndShowComponent"
    class="hideAndShow"
    v-if="$props.disableAnimation === true"
  >
    <div ref="ref_hideAndShowInner" class="hideAndShowWrapper">
      <div class="hideAndShowBody">
        <slot />
      </div>
    </div>
  </div>
  <div
    v-else
    ref="ref_hideAndShowComponent"
    class="hideAndShow"
    @mouseenter="onMouseEnter"
    @mouseleave="stopDelayedAutoScroll"
    @touchstart="onTouchStart"
  >
    <div
      ref="ref_hideAndShowInner"
      class="hideAndShowWrapper"
      :style="scrollStyle"
      :class="{ autoScroll: isScrolling }"
    >
      <div class="hideAndShowBody">
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="less">
@import "../styles/components/bc-hide-and-show.less";
</style>
